import React, { useContext, useEffect, useRef } from 'react';
import _merge from 'lodash/merge';
import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';
import { isFirefox } from 'react-device-detect';
import ResizeHandleIcon from '@nokia-csf-uxr/ccfk-assets/ResizeHandleIcon';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import { SPACING_16, SPACING_4, SPACING_24 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import { getContext, useCombinedRefs } from 'common';
import IconButton, { IconButtonProps } from 'components/IconButton';
import { LabelContext } from 'components/Label';
import Field from '../Field';
import TextAreaLabelContent from './TextAreaLabelContent';
import TextAreaCharacterCount from './TextAreaCharacterCount';
import TextAreaErrorMessage from './TextAreaErrorMessage';

import Styled from './styles';

const DEFAULT_ROWS = 3;
const DEFAULT_RESIZE_MODE = 'none';
const REM_BASE = 16;
const RADIUS = '0.125rem';

const DEFAULT_TEXT_AREA_STYLE = props => ({
  boxSizing: 'border-box' as const,
  flex: 'initial',
  width: props.cols !== undefined ? 'initial' : '100%',
  minHeight: '2rem',
  overflowX: 'hidden' as const,
  overflowY: 'auto' as const,
  borderRadius: props.variant === 'outlined' ? RADIUS : '0',
  padding: props.readOnly
    ? '0.5rem 0'
    : props.variant === 'outlined' ? '0.5rem' : '0.5rem 0',
  lineHeight: '1.313rem',
});

export interface TextAreaProps extends React.ComponentPropsWithRef<'textarea'> {
  /**
   * Callback fired when the value is changed. Signature: `function(event: object) => void`,
   * event: The event source of the callback. The new value: boolean `event.target.value`.
   */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  /** The value of the `textarea` element, required to control the component. Note: The native `textarea` element's `value` attribute will be set using this prop. */
  value: string;
  /** The variant of the input. */
  variant?: 'outlined' | 'underlined';
  /** If `true`, the component will be disabled. Note: The native `textarea` element's `disabled` attribute wills be set using this prop. Note: If the `disabled` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  disabled?: boolean;
  /** If `true`, the field cannot be edited by the user. Note: The native `textarea` element's `readonly` attribute will be set using this prop. Note: If the `readOnly` prop is not set, it will be internally taken from the immediate parent `Label` component. */
  readOnly?: boolean;
  /** The short hint displayed in the `textarea` when it is empty. Note: The native `textarea` element's `placeholder` attribute will be set using this prop. */
  placeholder?: string;
  /** If `true`, the component will automatically get focus when the page loads. Note: The native `textarea` element's `autofocus` attribute will be set using this prop. */
  autoFocus?: boolean;
  /** If `true`, the `textarea` will be rendered in the error state. (Note: TextArea can be in error state if `errorMessage` is set.) */
  error?: boolean;
  /** If set, the message will be displayed in the error state. `Typography` component can be used. */
  errorMessage?: React.ReactNode;
  /** If `true`, the max-width will be `31.25rem`. */
  maxWidth?: boolean;
  /** If `true`, the clear button will show. */
  showClearButton?: boolean;
  /** Props just for clear button. */
  clearButtonProps?: Partial<IconButtonProps>;
  /** Render a custom icon (to replace the default clear icon). See <IconButton /> and <Icon/> component for more information. */
  clearButtonIcon?: React.ReactNode;
  /** To specify an exact height (actually the "initial height" if `resize` is set) of the `textarea` in the number of rows. The native `textarea` element's `rows` attribute will be set using this prop. Note: `rows` prop is only applied to the `outlined` variant. */
  rows?: number;
  /** To specify an exact width (actually the "initial width" if `resize` is set) of the `textarea` in the number of columns. By default, `TextArea` fills the container's width respecting `maxWidth` prop. */
  cols?: number;
  /** Specify how the end-user can resize the height or the width of the `textarea`. `resize` will be `none` by disabled `TextArea`. */
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  /** If set, the character count will be rendered next to the field. */
  characterCount?: string | React.ReactNode;
  /**
   * DOM attributes applied to the `textarea` element. More info: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea Validation is controlled by you and can be implemented by evaluating the value of the textarea, thus configuring `maxLength` and `minLength` attributes does enable the field validation. Note: DOM attributes should be camelCased.
   */
  textareaProps?: Partial<React.ComponentPropsWithRef<'textarea'>>;
  /** To override the textarea theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Uncontrolled `TextArea` component. Any other props than the `TextArea` props will be provided to the root element.
 *  To alter the DOM attributes applied to the internal `textarea` element, please use `textareaProps`. To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference. */
const TextArea = React.forwardRef<HTMLElement, TextAreaProps>((props: TextAreaProps, ref) => getContext(({ RTL, theme }) => {
  const {
    onChange,
    value,
    disabled,
    readOnly,
    placeholder,
    textareaProps,
    errorMessage,
    autoFocus,
    error,
    rows,
    cols,
    resize,
    variant,
    characterCount,
    showClearButton,
    clearButtonProps,
    clearButtonIcon,
    theme: propTheme,
    ...wrapperProps
  } = props;

  const resolvedTheme = _merge({}, variant === 'outlined' ?
    theme.TEXT_AREA.OUTLINED :
    theme.TEXT_AREA.UNDERLINED, propTheme);

  const { style: fieldStyle, ref: fieldRef, ...otherFieldProps } = textareaProps;
  const [fieldEl, { width: fieldWidth }] = useMeasure({ polyfill: ResizeObserver });
  const combinedFieldRef = useCombinedRefs(fieldRef, fieldEl);

  const labelContext = useContext(LabelContext);
  const resolvedDisabled = props.disabled !== undefined ? props.disabled : labelContext.disabled;
  const resolvedReadOnly = props.readOnly !== undefined ? props.readOnly : labelContext.readOnly;

  let resolvedFieldElStyle = fieldStyle ? {
    ...DEFAULT_TEXT_AREA_STYLE({ ...props, disabled: resolvedDisabled, readOnly: resolvedReadOnly }),
    ...fieldStyle,
    resize: resolvedDisabled ? 'none' : resize
  } : {
    ...DEFAULT_TEXT_AREA_STYLE({ ...props, disabled: resolvedDisabled, readOnly: resolvedReadOnly }),
    resize: resolvedDisabled ? 'none' : resize
  };
  if (variant === 'outlined' && showClearButton) {
    const FIELD_PADDING = RTL ? { paddingLeft: SPACING_24 } : { paddingRight: SPACING_24 };
    resolvedFieldElStyle = _merge({}, resolvedFieldElStyle, FIELD_PADDING);
  }

  const resolvedFieldElProps = {
    ...otherFieldProps,
    as: 'textarea',
    ref: combinedFieldRef,
    style: resolvedFieldElStyle
  };

  const lastUnderlinedHeight = useRef<number | null>();

  const resolvedRows = variant === 'underlined' ? value.split(/\r\n|\r|\n/).length : rows;

  const fieldLineHeight = `${resolvedFieldElStyle.lineHeight}`;
  let fieldLineHeightInPixel:number;
  if (fieldLineHeight && fieldLineHeight.indexOf('rem') !== -1) {
    fieldLineHeightInPixel = parseInt(fieldLineHeight.replace('rem', ''), 10) * REM_BASE;
  } else if (fieldLineHeight && fieldLineHeight.indexOf('px') !== -1) {
    fieldLineHeightInPixel = parseInt(fieldLineHeight.replace('px', ''), 10) * 1;
  }

  const pivotHeight = resolvedFieldElStyle.height || '2.3125rem';

  // auto-growing height
  useEffect(() => {
    if (variant === 'underlined' && combinedFieldRef.current && value) {
      const maxHeight =
      resolvedFieldElStyle.maxHeight && resolvedFieldElStyle.maxHeight <= combinedFieldRef.current.scrollHeight
        ? resolvedFieldElStyle.maxHeight
        : combinedFieldRef.current.scrollHeight;
      combinedFieldRef.current.style.minHeight = pivotHeight;

      // for growing, set height to be scrollHeight
      if (!lastUnderlinedHeight.current) {
        lastUnderlinedHeight.current = maxHeight;
      } else if (maxHeight > lastUnderlinedHeight.current) {
        combinedFieldRef.current.style.height = `${maxHeight / 16}rem`;
        lastUnderlinedHeight.current = maxHeight;
      // for shrinking, subtract current height with lineHeight
      } else if (maxHeight < lastUnderlinedHeight.current) {
        combinedFieldRef.current.style.height = `${(lastUnderlinedHeight.current - fieldLineHeightInPixel) / 16}rem`;
        lastUnderlinedHeight.current -= fieldLineHeightInPixel;
      }
    }
  }, [combinedFieldRef.current, value]);

  const resolvedInputProps = {
    ...resolvedFieldElProps,
    onChange,
    value,
    disabled: resolvedDisabled,
    readOnly: resolvedReadOnly,
    placeholder,
    error: !!errorMessage || error,
    autoFocus,
    rows: resolvedRows,
    cols,
    variant,
    theme: resolvedTheme
  };

  const resolvedWrapperProps = {
    resize,
    ...wrapperProps
  };

  const resizeIconStyle = _merge(
    {},
    {
      position: 'absolute' as const,
      bottom: SPACING_4,
      pointerEvents: 'none' as const,
      display: fieldWidth === 0 ? 'none' : 'block'
    },
    RTL ? { right: `${(fieldWidth - 20) / 16}rem` } : { left: `${(fieldWidth - 20) / 16}rem` }
  );

  const clearButtonPosition = _merge(
    {},
    { top: SPACING_4,
      position: 'absolute' as const,
      display: fieldWidth === 0 ? 'none' : 'block'
    },
    RTL ? { right: `${(fieldWidth - 24) / 16}rem` } : { left: `${(fieldWidth - 24) / 16}rem` }
  );

  return (
    <Styled.Wrapper {...resolvedWrapperProps} RTL={RTL} ref={ref}>
      <Styled.RowWrapper theme={resolvedTheme}>
        <Field {...resolvedInputProps} />
        {variant === 'outlined' && showClearButton && value.length > 0 &&
        (
          <IconButton size="small" rippleStyle={clearButtonPosition} {...clearButtonProps}>
            { clearButtonIcon || <CloseIcon /> }
          </IconButton>
        )}
        { resize !== DEFAULT_RESIZE_MODE && !isFirefox && (
          <ResizeHandleIcon
            size={{ width: SPACING_16, height: SPACING_16 }}
            style={resizeIconStyle}
          />
        )}
      </Styled.RowWrapper>
      <Styled.MetaContainer $rtl={RTL} fieldWidth={fieldWidth}>
        {typeof errorMessage === 'string' ? <TextAreaErrorMessage>{errorMessage}</TextAreaErrorMessage> : errorMessage}
        {typeof characterCount === 'string' ? <TextAreaCharacterCount>{characterCount}</TextAreaCharacterCount> : characterCount}
      </Styled.MetaContainer>
    </Styled.Wrapper>
  );
}));

TextArea.displayName = 'TextArea';

TextArea.defaultProps = {
  onChange: undefined,
  variant: 'outlined',
  disabled: undefined, // `disabled`, by default, needs to be undefined to find is it configured by user or not.
  readOnly: undefined, // `readOnly`, by default, needs to be undefined to find is it configured by user or not.
  placeholder: undefined,
  autoFocus: false,
  showClearButton: false,
  clearButtonProps: {},
  clearButtonIcon: undefined,
  error: false,
  errorMessage: undefined,
  maxWidth: false,
  rows: DEFAULT_ROWS,
  cols: undefined,
  resize: DEFAULT_RESIZE_MODE,
  characterCount: undefined,
  textareaProps: {},
  theme: {}
};

TextArea.displayName = 'TextArea';

export {
  TextArea as default,
  TextAreaLabelContent,
  TextAreaErrorMessage,
  TextAreaCharacterCount
};
